import { useImperativeHandle, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";

export default function ExercisePicker({ library, value, onChange, onPick, onAddNew, onOpen, onRevert, pickerRef, testid, className }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  // Focus alone won't reopen the list when the input kept focus through the click.
  useImperativeHandle(pickerRef, () => ({ focus: () => { inputRef.current?.focus(); setOpen(true); } }), []);
  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) onRevert?.();
  };
  const query = String(value || "").trim().toLowerCase();
  const canAdd = Boolean(onAddNew && library && query && !library.some((exercise) => exercise.name.trim().toLowerCase() === query));
  const groups = (library || []).filter((exercise) => exercise.name.toLowerCase().includes(query)).reduce((result, exercise) => {
    const muscle = exercise.primary_muscle || exercise.muscle_group || "Other";
    result.set(muscle, [...(result.get(muscle) || []), exercise]);
    return result;
  }, new Map());
  const sortedGroups = [...groups.entries()].sort(([first], [second]) => first.localeCompare(second));
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <Input ref={inputRef} value={value} placeholder="Exercise" onChange={(event) => onChange(event.target.value)} onFocus={() => { if (!open) onOpen?.(); setOpen(true); }} className={className} data-testid={testid} />
      </PopoverAnchor>
      <PopoverContent className="p-0 w-56 bg-[#0B1220] border-[#1E293B]" align="start" onOpenAutoFocus={(event) => event.preventDefault()} onInteractOutside={(event) => { if (event.target === inputRef.current) event.preventDefault(); }}>
        <Command shouldFilter={false}>
          <CommandList>
            <CommandEmpty>{library ? "No exercises found." : "Library unavailable. Try again shortly."}</CommandEmpty>
            {canAdd && (
              <CommandGroup>
                <CommandItem value="add-new-exercise" onSelect={() => { onAddNew(String(value).trim()); setOpen(false); }} className="text-[#FF3B30]" data-testid={`add-to-library-${testid}`}>
                  + Add "{String(value).trim()}" to library
                </CommandItem>
              </CommandGroup>
            )}
            {sortedGroups.map(([muscle, exercises]) => (
              <CommandGroup key={muscle} heading={muscle} className="[&_[cmdk-group-heading]]:text-[#FF3B30]">
                {exercises.sort((first, second) => first.name.localeCompare(second.name)).map((exercise) => (
                  <CommandItem key={exercise.id} value={exercise.name} onSelect={() => { onPick(exercise); setOpen(false); }} data-testid={`pick-exercise-${exercise.id}`}>
                    {exercise.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
